import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Award, GraduationCap, Wrench, BookOpen, MapPin, Phone } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const SITE_URL = 'https://energyperformancedirect.co.uk';

const AuthorZakHenning = () => {
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${SITE_URL}/author/zak-henning#person`,
    name: 'Zak Henning',
    givenName: 'Zak',
    familyName: 'Henning',
    jobTitle: 'Managing Director & Energy Assessor',
    description:
      'Managing Director of Energy Performance Direct, qualified Domestic and Non-Domestic Energy Assessor, Retrofit Assessor and Retrofit Coordinator serving the South West of England since 2009.',
    worksFor: { '@id': `${SITE_URL}/#organization` },
    url: `${SITE_URL}/author/zak-henning`,
    knowsAbout: [
      'Energy Performance Certificates (EPC)',
      'Non-Domestic Energy Performance Certificates (NDEPC)',
      'SAP Calculations',
      'SBEM Calculations',
      'RdSAP Assessments',
      'MEES Compliance',
      'PAS 2035 Retrofit Coordination',
      'Building Energy Modelling',
      'League Table & Rafts Methodology',
    ],
    hasCredential: [
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'certification', name: 'Quidos-accredited Domestic Energy Assessor' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'certification', name: 'Quidos-accredited Non-Domestic Energy Assessor (NDEA Level 3)' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'certification', name: 'PAS 2035 Retrofit Assessor' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'certification', name: 'PAS 2035 Retrofit Coordinator' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'certification', name: 'On-Construction Domestic Energy Assessor (OCDEA)' },
    ],
    areaServed: ['Devon', 'Cornwall', 'Somerset', 'Dorset', 'South West England'],
    telephone: '+441392980077',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Author', item: `${SITE_URL}/author/zak-henning` },
    ],
  };

  const credentials = [
    { icon: <Award className="w-5 h-5" />, name: 'Quidos NDEA (Non-Domestic Energy Assessor)', detail: 'Accreditation QUID201039 — Level 3 commercial EPCs, SBEM modelling, MEES advice.' },
    { icon: <Award className="w-5 h-5" />, name: 'Quidos DEA (Domestic Energy Assessor)', detail: 'RdSAP 10 assessments for domestic property, sales, lettings and MEES.' },
    { icon: <Award className="w-5 h-5" />, name: 'OCDEA (On-Construction Domestic Energy Assessor)', detail: 'SAP-based new-build compliance and as-built EPC lodgement.' },
    { icon: <GraduationCap className="w-5 h-5" />, name: 'PAS 2035 Retrofit Assessor', detail: 'Whole-house assessments for domestic retrofit projects.' },
    { icon: <GraduationCap className="w-5 h-5" />, name: 'PAS 2035 Retrofit Coordinator', detail: 'Retrofit project coordination under BSI PAS 2035:2023.' },
  ];

  const publishedWork = [
    { title: 'The League Table & Rafts Methodology', path: '/methodology', summary: 'Proprietary five-criterion measure-ranking framework used in every DEAR and NDEAR advice report.' },
    { title: 'Exeter G→A period office case study', path: '/case-study-exeter-office-g-to-a', summary: '364.9 m² Grade II-listed Victorian office modelled from G189 baseline to A25 through five costed pathways.' },
    { title: 'Sticklepath SAP conversion case study', path: '/case-study-sticklepath-sap-conversion', summary: 'Retrospective SAP 10.2 on a 36.1 m² Devon stone outbuilding conversion — reflects on what an assessor engagement at design stage would have unlocked.' },
    { title: 'London MEES unit-split case study', path: '/case-study-london-mees-unit-split', summary: 'How a 431 m² G242 London commercial building was staged through three units to Band B compliance.' },
    { title: 'SBEM Explained — the engine behind every Commercial EPC', path: '/sbem-explained', summary: 'Working guide to iSBEM, its inputs, fabric vs services levers and where DSM takes over.' },
    { title: 'MEES for Commercial Buildings — landlord\'s compliance guide', path: '/mees-commercial-buildings', summary: 'Current Band E statutory minimum (April 2023), the June 2026 DESNZ interim response (proposed EPC B by 2031 for buildings over 1,000 m², 2027 EPC C dropped), the 7-year payback test and the registered exemptions.' },
  ];

  return (
    <>
      <Helmet>
        <title>Zak Henning — Energy Assessor & Retrofit Coordinator | Energy Performance Direct</title>
        <meta
          name="description"
          content="Author dossier for Zak Henning, Managing Director of Energy Performance Direct. Quidos-accredited Domestic & Non-Domestic Energy Assessor, PAS 2035 Retrofit Coordinator, based in Exeter serving the South West since 2009."
        />
        <link rel="canonical" href={`${SITE_URL}/author/zak-henning`} />
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white" data-testid="author-page">
        <Navbar />
        {/* Hero */}
        <section
          className="py-16 lg:py-20"
          style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #cffafe 50%, #ecfdf5 100%)' }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Author dossier · E-E-A-T</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6" data-testid="author-h1">
                Zak Henning
              </h1>
              <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-3xl">
                Managing Director of Energy Performance Direct. Quidos-accredited Domestic and Non-Domestic Energy
                Assessor. PAS 2035 Retrofit Coordinator. Author of the proprietary{' '}
                <Link to="/methodology" className="text-green-700 underline">
                  League Table &amp; Rafts methodology
                </Link>{' '}
                used in every DEAR and NDEAR advice report we lodge.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="inline-flex items-center gap-2 text-sm text-gray-700 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <MapPin className="w-4 h-4 text-green-600" /> Based in Exeter, Devon
                </div>
                <div className="inline-flex items-center gap-2 text-sm text-gray-700 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <BookOpen className="w-4 h-4 text-green-600" /> Practising since 2009
                </div>
                <div className="inline-flex items-center gap-2 text-sm text-gray-700 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <Phone className="w-4 h-4 text-green-600" /> 01392 980077
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Biography */}
        <section className="py-14">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Biography</h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>
                  Zak Henning founded Energy Performance Direct in 2009 and has led the practice for over 15 years. Based
                  in Exeter, he works across Devon, Cornwall, Somerset and Dorset — plus the Bristol / Bath / North
                  Somerset corridor — producing Domestic and Commercial EPCs, SAP and SBEM calculations, and structured
                  Energy Advice Reports (DEAR &amp; NDEAR) for landlords, developers, homeowners and portfolio holders.
                </p>
                <p>
                  Zak trained originally as a Domestic Energy Assessor in the early years of the UK EPC regime, adding
                  non-domestic accreditation as the commercial market matured, and — more recently — PAS 2035 Retrofit
                  Assessor and Retrofit Coordinator qualifications. That progression reflects a deliberate move away
                  from single-service EPC lodgement toward{' '}
                  <strong>independent building energy consultancy</strong>: modelling, ranking and staging improvement
                  measures rather than issuing static recommendation lists.
                </p>
                <p>
                  A whole-house retrofit of his own home — improving it from D to A — provided hands-on experience with
                  external wall insulation, secondary glazing, air-source heat pump retrofit, solar PV, MVHR and
                  thermal-bridge mitigation. That project informed the practical, budget-realistic bias in every advice
                  report the practice produces.
                </p>
                <p>
                  Zak was also actively involved in the Green Deal during its operation, supporting property owners
                  through energy-efficiency upgrades — an experience which shaped the practice&apos;s current view that
                  measure ranking must always surface £/yr, £ capital, payback and EPC-band trade-offs rather than a
                  one-number carbon score.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Credentials */}
        <section className="py-14 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Credentials &amp; accreditations</h2>
              <div className="grid gap-4">
                {credentials.map((c, i) => (
                  <Card key={i} data-testid={`credential-${i}`} className="border-l-4 border-green-600">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700">
                          {c.icon}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{c.name}</h3>
                          <p className="text-sm text-gray-700 mt-1">{c.detail}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-6">
                All energy assessments are lodged on the government EPC Register under Quidos accreditation{' '}
                <strong>QUID201039</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Published work */}
        <section className="py-14">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Published work &amp; case studies</h2>
              <p className="text-gray-700 mb-8">
                A selection of methodology writeups and worked-example case studies authored on this site.
              </p>
              <div className="grid gap-4">
                {publishedWork.map((item, i) => (
                  <Card key={i} data-testid={`work-${i}`} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700">
                          <Wrench className="w-5 h-5" />
                        </div>
                        <div className="flex-grow">
                          <h3 className="text-lg font-semibold text-gray-900">
                            <Link to={item.path} className="hover:text-green-700">
                              {item.title}
                            </Link>
                          </h3>
                          <p className="text-sm text-gray-700 mt-1">{item.summary}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Work directly with Zak</h2>
              <p className="text-gray-700 mb-6">
                Every DEAR and NDEAR report we produce is authored, modelled and reviewed by Zak personally.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg" data-testid="author-quote-btn">
                  <Link to="/quick-quote?source=author">Request a quote</Link>
                </Button>
                <Button asChild variant="outline" size="lg" data-testid="author-methodology-btn">
                  <Link to="/methodology">Read the methodology</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default AuthorZakHenning;
